import React from 'react'
import { Button, Input, Space } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import { ConformityTableType, DataIndex } from './types'

const legalStatusList = [
  { text: 'Artisan', value: 'Artisan' },
  { text: 'SRL', value: 'SRL' },
  { text: 'Association', value: 'Association' },
  { text: 'SA', value: 'SA' },
  { text: 'Auto entrepreneur', value: 'Auto entrepreneur' },
  { text: 'SPRL', value: 'SPRL' },
  { text: 'Bureau de liaison', value: 'Bureau de liaison' },
  { text: 'Société simple', value: 'Société simple' },
  { text: 'EIRL', value: 'EIRL' },
  { text: 'Société coopérative (SC)', value: 'Société coopérative (SC)' },
  { text: 'EURL', value: 'EURL' },
  { text: 'Entreprise individuelle', value: 'Entreprise individuelle' },
  { text: 'GIE', value: 'GIE' },
  { text: 'Ltd', value: 'Ltd' },
  { text: 'Micro entrepreneur', value: 'Micro entrepreneur' },
  { text: 'Profession libérale', value: 'Profession libérale' },
  { text: 'SARL', value: 'SARL' },
  { text: 'SAS', value: 'SAS' },
  { text: 'SASU', value: 'SASU' },
  { text: 'SCI', value: 'SCI' },
  { text: 'SCIC', value: 'SCIC' },
  { text: 'SEP', value: 'SEP' },
  { text: 'SNC', value: 'SNC' },
  { text: 'Succursale', value: 'Succursale' },
  { text: 'Syndicat', value: 'Syndicat' },
  { text: 'SICAV', value: 'SICAV' },
  { text: 'SCCV', value: 'SCCV' },
  { text: 'SC', value: 'SC' },
  { text: 'SCOP SARL', value: 'SCOP SARL' },
  { text: 'SCP', value: 'SCP' },
  { text: 'SCM', value: 'SCM' },
  { text: 'Établissement secondaire', value: 'Établissement secondaire' },
  { text: 'Autre', value: 'Autre' },
  { text: 'SPRL-S', value: 'SPRL-S' },
  { text: 'SCRI', value: 'SCRI' },
  { text: 'SCRL', value: 'SCRL' },
  { text: 'SCS', value: 'SCS' },
  { text: 'SCA', value: 'SCA' },
  { text: 'ASBL', value: 'ASBL' },
  { text: 'AISBL', value: 'AISBL' },
  { text: 'Indépendant en EI', value: 'Indépendant en EI' },
  { text: 'Indépendant en société', value: 'Indépendant en société' },
]

export const getLegalStatusColumnSearchProps = (
  variables: any,
  dataIndex: DataIndex
): ColumnType<ConformityTableType> => ({
  filtered: !!variables.filters?.[`${dataIndex}`],
  filteredValue: variables.filters?.[`${dataIndex}`],
  filters: legalStatusList,
})

export const handleSearch = (
  selectedKeys: string[],
  confirm: (param?: any) => void
) => confirm()
export const handleReset = (clearFilters: () => void) => clearFilters()

export const getColumnSearchProps = (
  variables: any,
  dataIndex: DataIndex
): ColumnType<ConformityTableType> => ({
  filtered: !!variables.filters?.[`${dataIndex}`],
  filteredValue: variables.filters?.[`${dataIndex}`],
  // eslint-disable-next-line react/display-name
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }: any) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={`Search ${dataIndex}`}
        value={selectedKeys[0]}
        onChange={(e: any) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() =>
          handleSearch(selectedKeys as string[], confirm, dataIndex)
        }
        style={{ marginBottom: 8, display: 'block' }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={() => clearFilters && handleReset(clearFilters)}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </Space>
    </div>
  ),
  // eslint-disable-next-line react/display-name
  filterIcon: (filtered: boolean) => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
})

const columnSortOrder = (variables: any, columnName: string) =>
  variables.sorter?.field === columnName ? variables.sorter.order : false

const adminColumns = (t: any, variables: any, canFullAccess: boolean) =>
  canFullAccess
    ? [
        {
          title: t('conformities:columns.moderator'),
          dataIndex: 'moderator',
          key: 'moderator',
          sorter: true,
          sortOrder: columnSortOrder(variables, 'moderator'),
        },
        {
          title: t('conformities:columns.previous-moderator'),
          dataIndex: 'previousModerator',
          key: 'previousModerator',
          sorter: true,
          sortOrder: columnSortOrder(variables, 'previousModerator'),
        },
        {
          title: t('conformities:columns.checked-at'),
          dataIndex: 'checkedAt',
          key: 'checkedAt',
          sorter: true,
          sortOrder: columnSortOrder(variables, 'checkedAt'),
        },
      ]
    : []

export const columns = (t: any, variables: any, canFullAccess: boolean) => [
  {
    title: 'Id',
    dataIndex: 'domiciliationId',
    key: 'domiciliationId',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'domiciliationId'),
    ...getColumnSearchProps(variables, 'domiciliationId'),
  },
  {
    title: 'Kbis',
    dataIndex: 'kbis',
    key: 'kbis',
  },
  {
    title: t('conformities:columns.address-domiciliation'),
    dataIndex: 'address',
    key: 'address',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'address'),
  },
  {
    title: t('conformities:columns.society-name'),
    dataIndex: 'societyName',
    key: 'societyName',
    ...getColumnSearchProps(variables, 'societyName'),
  },
  {
    title: t('conformities:columns.society-brandname'),
    dataIndex: 'societyBrandName',
    key: 'societyBrandName',
  },
  {
    title: t('firstname'),
    dataIndex: 'firstname',
    key: 'firstname',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'firstname'),
  },
  {
    title: t('lastname'),
    dataIndex: 'lastname',
    key: 'lastname',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'lastname'),
  },
  {
    title: t('conformities:columns.legal-status'),
    dataIndex: 'legalStatus',
    key: 'legalStatus',
    ...getLegalStatusColumnSearchProps(variables, 'legalStatus'),
  },
  {
    title: t('conformities:columns.society-type'),
    dataIndex: 'societyType',
    key: 'societyType',
  },
  {
    title: t('conformities:columns.signature-date'),
    dataIndex: 'clientSince',
    key: 'clientSince',
    sorter: true,
    sortOrder: columnSortOrder(variables, 'clientSince'),
  },
  {
    title: t('conformities:columns.siren'),
    dataIndex: 'siren',
    key: 'siren',
    ...getColumnSearchProps(variables, 'siren'),
  },
  ...adminColumns(t, variables, canFullAccess),
]
